/**
 * WritingTab — AI-assisted content generation.
 *
 * Flow:
 *  1. Select a prompt library
 *  2. Choose a prompt (shows section-match badge if set)
 *  3. Fill in declared template variables
 *  4. Generate — submits job, polls for result
 *  5. Insert at cursor
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Input,
  Label,
  Badge,
  Separator,
  Alert,
  AlertDescription,
} from '@e14c/feathers';
import { Renew, ChevronDown, ChevronUp, Edit } from '@e14c/feathers/icons';

import { useWriteStore } from '../store';
import {
  listLibraries,
  listPrompts,
  submitGeneration,
  pollGeneration,
  getUserSettings,
  ARIES_POLL_TIMEOUT_MS,
  PAPERQA_POLL_TIMEOUT_MS,
  DEFAULT_POLL_TIMEOUT_MS,
} from '../api/contentGen';
import type { PromptOut } from '../types';
import { GENERATION_APPROACH_LABELS } from '../types';
import { useTabLayoutStyles } from '../styles/tabLayout';
import { TabScrollArea } from '../components/TabScrollArea';

// ---------------------------------------------------------------------------
// Inline spinner
// ---------------------------------------------------------------------------

const Spinner: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <span
    style={{ width: size, height: size }}
    className="inline-block animate-spin rounded-full border-2 border-current border-t-transparent"
  />
);

// ---------------------------------------------------------------------------
// Prompt row
// ---------------------------------------------------------------------------

interface PromptRowProps {
  prompt: PromptOut;
  isSelected: boolean;
  onSelect: () => void;
  variables: Record<string, string>;
  onVariableChange: (name: string, value: string) => void;
  onGenerate: (promptId: string) => void;
  isGenerating: boolean;
  generationProgress: string[];
}

const PromptRow: React.FC<PromptRowProps> = ({
  prompt,
  isSelected,
  onSelect,
  variables,
  onVariableChange,
  onGenerate,
  isGenerating,
  generationProgress,
}) => {
  const progressEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isGenerating && generationProgress.length > 0) {
      progressEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [generationProgress.length, isGenerating]);
  const matchBadge = (): React.ReactNode => {
    if (!prompt.section_match_type || !prompt.section_match) return null;
    const label =
      prompt.section_match_type === 'exact'
        ? `= ${prompt.section_match}`
        : prompt.section_match_type === 'regex'
          ? `~ ${prompt.section_match}`
          : 'any';
    return <Badge variant="secondary">{label}</Badge>;
  };

  const serviceLabel =
    GENERATION_APPROACH_LABELS[prompt.approach ?? 'llm'];

  const canGenerate = prompt.variables
    .filter((v) => v.required)
    .every((v) => (variables[v.name] ?? '').trim().length > 0);

  const handleGenerateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onGenerate(prompt.id);
  };

  const showAriesProgress =
    isGenerating && prompt.approach === 'aries';

  return (
    <div
      className={`rounded-md overflow-hidden border transition-colors ${
        isSelected ? 'border-primary' : 'border-border'
      }`}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2 cursor-pointer ${
          isSelected ? 'bg-primary/5 hover:bg-primary/10' : 'bg-background hover:bg-muted/40'
        }`}
        onClick={onSelect}
        role="button"
        aria-expanded={isSelected}
      >
        <span className="flex-1 text-sm font-semibold truncate">
          {prompt.title}
        </span>
        <Badge variant="outline">{serviceLabel}</Badge>
        {matchBadge()}
        {isSelected ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>

      {isSelected && (
        <div className="px-3 py-2 bg-muted/30 border-t border-border flex flex-col gap-3">
          {/* Template preview */}
          <pre className="text-xs text-muted-foreground font-mono bg-muted rounded px-2 py-1.5 whitespace-pre-wrap break-words">
            {prompt.template.length > 200
              ? prompt.template.slice(0, 200) + '…'
              : prompt.template}
          </pre>

          {/* Variables */}
          {prompt.variables.length > 0 && (
            <>
              <Separator />
              {prompt.variables.map((v) => (
                <div key={v.name} className="space-y-1.5">
                  <Label>
                    {v.name}
                    {v.required && (
                      <span className="text-destructive ml-0.5">*</span>
                    )}
                  </Label>
                  <Input
                    value={variables[v.name] ?? ''}
                    onChange={(e) => onVariableChange(v.name, e.target.value)}
                    placeholder={v.default ?? `Enter ${v.name}`}
                    disabled={isGenerating}
                  />
                  {v.description && (
                    <p className="text-xs text-muted-foreground">
                      {v.description}
                    </p>
                  )}
                </div>
              ))}
            </>
          )}

          {showAriesProgress && (
            <div
              className="min-h-[72px] max-h-[120px] overflow-y-auto rounded-md border border-border bg-background px-2 py-1.5 flex flex-col gap-1"
              aria-live="polite"
              aria-label="Generation progress"
            >
              {generationProgress.length === 0 ? (
                <p className="text-xs leading-relaxed text-muted-foreground flex items-center gap-1.5">
                  <Spinner size={12} />
                  Starting…
                </p>
              ) : (
                generationProgress.map((msg, index) => (
                  <p
                    key={`${index}-${msg.slice(0, 32)}`}
                    className={`text-xs leading-relaxed ${
                      index === generationProgress.length - 1
                        ? 'text-foreground'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {msg}
                  </p>
                ))
              )}
              <div ref={progressEndRef} />
            </div>
          )}

          <div className="flex justify-end">
            {isGenerating ? (
              <Button
                key="generating"
                size="sm"
                variant="secondary"
                disabled
                aria-busy="true"
              >
                <Spinner size={14} />
                Generating…
              </Button>
            ) : (
              <Button
                key="generate"
                size="sm"
                onClick={handleGenerateClick}
                disabled={!canGenerate}
              >
                {`Generate with ${serviceLabel}`}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const WritingTab: React.FC = () => {
  const layout = useTabLayoutStyles();

  const libraries = useWriteStore((s) => s.libraries);
  const selectedLibraryId = useWriteStore((s) => s.selectedLibraryId);
  const prompts = useWriteStore((s) => s.prompts);
  const selectedPromptId = useWriteStore((s) => s.selectedPromptId);
  const variables = useWriteStore((s) => s.variables);
  const isLoadingLibraries = useWriteStore((s) => s.isLoadingLibraries);
  const isLoadingPrompts = useWriteStore((s) => s.isLoadingPrompts);
  const isGenerating = useWriteStore((s) => s.isGenerating);
  const result = useWriteStore((s) => s.result);
  const generationProgress = useWriteStore((s) => s.generationProgress);
  const writeError = useWriteStore((s) => s.writeError);

  const setLibraries = useWriteStore((s) => s.setLibraries);
  const setSelectedLibraryId = useWriteStore((s) => s.setSelectedLibraryId);
  const setPrompts = useWriteStore((s) => s.setPrompts);
  const setSelectedPromptId = useWriteStore((s) => s.setSelectedPromptId);
  const setVariable = useWriteStore((s) => s.setVariable);
  const setIsLoadingLibraries = useWriteStore((s) => s.setIsLoadingLibraries);
  const setIsLoadingPrompts = useWriteStore((s) => s.setIsLoadingPrompts);
  const setIsGenerating = useWriteStore((s) => s.setIsGenerating);
  const setGenerationProgress = useWriteStore((s) => s.setGenerationProgress);
  const setResult = useWriteStore((s) => s.setResult);
  const setWriteError = useWriteStore((s) => s.setWriteError);
  const clearResult = useWriteStore((s) => s.clearResult);

  const [insertStatus, setInsertStatus] = useState<'idle' | 'ok' | 'error'>('idle');
  const [defaultPaperIndexId, setDefaultPaperIndexId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    void getUserSettings()
      .then((settings) => {
        setDefaultPaperIndexId(settings.default_paper_index_id);
      })
      .catch(() => undefined);
  }, []);

  // ── Load libraries ─────────────────────────────────────────────────────────
  const loadLibraries = useCallback(async () => {
    setIsLoadingLibraries(true);
    setWriteError(null);
    try {
      const data = await listLibraries(50, 0);
      setLibraries(data.items);
    } catch (err: unknown) {
      setWriteError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoadingLibraries(false);
    }
  }, [setIsLoadingLibraries, setWriteError, setLibraries]);

  useEffect(() => {
    if (libraries.length === 0 && !isLoadingLibraries) {
      loadLibraries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load prompts ───────────────────────────────────────────────────────────
  const loadPrompts = useCallback(
    async (libraryId: string) => {
      setIsLoadingPrompts(true);
      setWriteError(null);
      try {
        const data = await listPrompts(libraryId, 100, 0);
        setPrompts(data.items);
      } catch (err: unknown) {
        setWriteError(err instanceof Error ? err.message : String(err));
      } finally {
        setIsLoadingPrompts(false);
      }
    },
    [setIsLoadingPrompts, setWriteError, setPrompts],
  );

  const handleLibraryChange = (id: string) => {
    setSelectedLibraryId(id || null);
    setPrompts([]);
    if (id) loadPrompts(id);
  };

  // ── Generate ───────────────────────────────────────────────────────────────
  const handleGenerate = useCallback(async (promptId: string) => {
    setSelectedPromptId(promptId);
    setIsGenerating(true);
    setGenerationProgress([]);
    setWriteError(null);
    clearResult();
    setInsertStatus('idle');

    const prompt = prompts.find((p) => p.id === promptId);
    const mergedVars: Record<string, string> = {};
    if (prompt) {
      for (const v of prompt.variables) {
        if (v.default != null) mergedVars[v.name] = v.default;
      }
    }
    Object.assign(mergedVars, variables);

    const approach = prompt?.approach ?? 'llm';
    const pollTimeout =
      approach === 'aries'
        ? ARIES_POLL_TIMEOUT_MS
        : approach === 'paperqa'
          ? PAPERQA_POLL_TIMEOUT_MS
          : DEFAULT_POLL_TIMEOUT_MS;

    if (approach === 'paperqa' && !defaultPaperIndexId) {
      setWriteError(
        'Select a default literature index in Manage → Literature Index.',
      );
      setIsGenerating(false);
      return;
    }

    try {
      const job = await submitGeneration({
        prompt_id: promptId,
        variables: mergedVars,
        approach,
        index_id: approach === 'paperqa' ? defaultPaperIndexId : undefined,
      });
      const completed = await pollGeneration(
        job.job_id,
        (data) => {
          const messages = data.progress?.messages;
          if (messages?.length) {
            setGenerationProgress(messages);
          }
        },
        pollTimeout,
      );
      if (completed.result) {
        setResult(completed.result);
      } else {
        setWriteError('Generation completed but returned no content.');
      }
    } catch (err: unknown) {
      setWriteError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsGenerating(false);
    }
  }, [
    prompts,
    variables,
    defaultPaperIndexId,
    setSelectedPromptId,
    setIsGenerating,
    setGenerationProgress,
    setWriteError,
    clearResult,
    setResult,
  ]);

  // ── Insert at cursor ───────────────────────────────────────────────────────
  const handleInsert = useCallback(async () => {
    if (!result) return;
    const text = result.sentences.map((s) => s.text).join(' ');
    try {
      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.insertText(text, Word.InsertLocation.replace);
        await context.sync();
      });
      setInsertStatus('ok');
    } catch (err: unknown) {
      console.error('Insert failed:', err);
      setInsertStatus('error');
    }
  }, [result]);

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoadingLibraries) {
    return (
      <div className={layout.panel}>
        <div className={layout.centered}>
          <span className="inline-block size-8 animate-spin rounded-full border-[3px] border-muted-foreground border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading libraries…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={layout.panel}>
      <TabScrollArea>
        {/* Error banner */}
        {writeError && (
          <Alert variant="destructive">
            <AlertDescription>{writeError}</AlertDescription>
          </Alert>
        )}

        {/* Library selector */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-semibold">Library</span>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={loadLibraries}
              disabled={isLoadingLibraries}
              title="Refresh libraries"
            >
              <Renew size={16} />
            </Button>
          </div>
          <select
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
            value={selectedLibraryId ?? ''}
            onChange={(e) => handleLibraryChange(e.target.value)}
            disabled={isLoadingLibraries}
          >
            <option value="">— Select a library —</option>
            {libraries.map((lib) => (
              <option key={lib.id} value={lib.id}>
                {lib.name} ({lib.prompt_count})
              </option>
            ))}
          </select>
        </div>

        {/* Prompts list */}
        {selectedLibraryId && (
          <div>
            <span className="text-sm font-semibold block mb-1.5">Prompts</span>

            {isLoadingPrompts && (
              <div className="flex flex-col items-center gap-2 py-6">
                <span className="inline-block size-6 animate-spin rounded-full border-[3px] border-muted-foreground border-t-transparent" />
                <span className="text-sm text-muted-foreground">
                  Loading prompts…
                </span>
              </div>
            )}

            {!isLoadingPrompts && prompts.length === 0 && (
              <p className="text-sm text-muted-foreground italic">
                No prompts in this library.
              </p>
            )}

            {!isLoadingPrompts &&
              prompts.map((p) => (
                <div key={p.id} className="mb-1.5">
                  <PromptRow
                    prompt={p}
                    isSelected={selectedPromptId === p.id}
                    onSelect={() =>
                      setSelectedPromptId(
                        selectedPromptId === p.id ? null : p.id,
                      )
                    }
                    variables={variables}
                    onVariableChange={setVariable}
                    onGenerate={handleGenerate}
                    isGenerating={isGenerating && selectedPromptId === p.id}
                    generationProgress={generationProgress}
                  />
                </div>
              ))}
          </div>
        )}

        {/* Generating overlay (when no prompt panel visible) */}
        {isGenerating && !prompts.find((p) => p.id === selectedPromptId) && (
          <div className="flex flex-col items-center gap-2 py-8">
            <span className="inline-block size-8 animate-spin rounded-full border-[3px] border-muted-foreground border-t-transparent" />
            <span className="text-sm text-muted-foreground">
              Generating content…
            </span>
          </div>
        )}

        {/* Generated result */}
        {result && !isGenerating && (
          <div className="border border-primary rounded-md p-3 bg-background flex flex-col gap-3">
            <p className="text-sm font-semibold">
              {result.section_title ?? 'Generated Content'}
            </p>

            <Separator />

            <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
              {result.sentences.map((s) => s.text).join(' ')}
            </p>

            {result.notes && (
              <p className="text-xs text-muted-foreground italic">
                {result.notes}
              </p>
            )}

            {insertStatus === 'ok' && (
              <Alert>
                <AlertDescription>Inserted into document.</AlertDescription>
              </Alert>
            )}
            {insertStatus === 'error' && (
              <Alert variant="destructive">
                <AlertDescription>
                  Insert failed — is a Word document open?
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button size="sm" onClick={handleInsert}>
                <Edit size={16} />
                Insert at cursor
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  clearResult();
                  setInsertStatus('idle');
                  if (selectedPromptId) handleGenerate(selectedPromptId);
                }}
                disabled={isGenerating}
              >
                <Renew size={16} />
                Regenerate
              </Button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!selectedLibraryId && !isLoadingLibraries && (
          <div className={layout.centered}>
            <Edit size={32} className="text-muted-foreground" />
            <p className="text-sm font-semibold text-muted-foreground">
              Select a library to begin
            </p>
            <p className="text-xs text-muted-foreground">
              Choose a prompt, fill in any variables, and click Generate.
            </p>
          </div>
        )}
      </TabScrollArea>
    </div>
  );
};
